/**
 * MeetingNotesOverviewEnrichment — Enriched overview rows for meeting notes.
 *
 * Fetches the associated meeting (via ahoy_meeting_notes_to_meeting) and
 * renders it as a clickable badge, along with meeting time and attendees.
 * Attendees follow the same pattern as MeetingSummary in summary-section.tsx:
 * resolved contacts render as EntityBadge, others as AttendeeChip.
 */

import { Clock, Link03, Users01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Avatar } from '@/lib/base/avatar/avatar';
import { getAvatarInitials, getGravatarUrl } from '@/lib/crm/utils/avatar';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { useOrgTimeFormat } from '@/lib/time';
import type { CalendarEventGuest } from '@/pages/calendar/utils/eventMapper';
import { mapInvitationsToGuests } from '@/pages/calendar/utils/eventMapper';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EntityBadge } from '../../components/badges/EntityBadge';
import type { EntityBadgeState } from '../../components/badges/types';
import { useAssociations } from '../../hooks/useAssociations';
import { useOpenSlideoutOptional } from '../../hooks/useOpenSlideout';
import { OverviewFieldRow } from '../components/OverviewFieldRow';
import {
  formatMeetingDateRange,
  getGuestDisplayName,
} from '../utils/meetingHelpers';

// ============================================================================
// Per-row badge: stable state + onClick per entity. Lives at module scope so
// the parent's `.map(guest => ...)` doesn't recreate this component each render.
// ============================================================================

function ClickableEntityBadge({
  object,
  onEntityClick,
}: {
  object: CrmObject;
  onEntityClick: ((entityId: string) => void) | undefined;
}) {
  const state = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: {
        id: object.entity_id,
        entityType: object.entity_type_name,
        displayName: getDisplayName(object),
      },
    }),
    [object]
  );
  const handleClick = useCallback(() => {
    onEntityClick?.(object.entity_id);
  }, [onEntityClick, object.entity_id]);
  return (
    <EntityBadge
      state={state}
      variant='xs'
      onClick={onEntityClick ? handleClick : undefined}
    />
  );
}

// ============================================================================
// Attendee chip (same pattern as summary-section.tsx AttendeeChip)
// ============================================================================

function AttendeeChip({ guest }: { guest: CalendarEventGuest }) {
  const displayName = getGuestDisplayName(guest);
  const gravatarUrl = getGravatarUrl(guest.email, 48);
  const tooltipText = `${guest.name || guest.email.split('@')[0]} · ${guest.email}`;

  return (
    <span
      className='size-max flex items-center gap-1 whitespace-nowrap rounded-full py-0.5 pl-1 pr-2 text-xs font-medium ring-1 ring-inset bg-utility-gray-50 text-utility-gray-700 ring-utility-gray-200 [&_[data-avatar]>span]:text-[9px]'
      title={tooltipText}
    >
      <Avatar
        size='xxs'
        src={gravatarUrl}
        alt={displayName}
        initials={getAvatarInitials(guest.name || guest.email)}
      />
      <span className='whitespace-nowrap'>{displayName}</span>
    </span>
  );
}

// ============================================================================
// Main component
// ============================================================================

interface MeetingNotesOverviewEnrichmentProps {
  object: CrmObject;
}

export function MeetingNotesOverviewEnrichment({
  object,
}: MeetingNotesOverviewEnrichmentProps) {
  const orgId = object.org_id;
  const openSlideout = useOpenSlideoutOptional();

  const handleEntityClick = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  // Resolve meeting notes associations to find the meeting
  const noteAssociations = useMemo(
    () => object.associations ?? [],
    [object.associations]
  );
  const { associations: resolvedNoteAssociations } = useAssociations(
    orgId,
    noteAssociations
  );

  const meetingObject = useMemo(() => {
    const meetingAssoc = resolvedNoteAssociations.find(
      a =>
        a.association_type_name === 'ahoy_meeting_notes_to_meeting' && a.object
    );
    return meetingAssoc?.object ?? null;
  }, [resolvedNoteAssociations]);

  // Resolve meeting's own associations (contacts/users for attendee matching)
  const meetingAssociations = useMemo(
    () => meetingObject?.associations ?? [],
    [meetingObject?.associations]
  );
  const { associations: resolvedMeetingAssociations } = useAssociations(
    orgId,
    meetingAssociations
  );

  const { members } = useOrgMembers(orgId);
  const { timeFormat } = useOrgTimeFormat();

  // Extract meeting properties
  const startTime = meetingObject
    ? (getPropertyValue<string>(meetingObject.properties?.ahoy_start_time) ??
      null)
    : null;
  const endTime = meetingObject
    ? (getPropertyValue<string>(meetingObject.properties?.ahoy_end_time) ??
      null)
    : null;

  const timeStr = useMemo(
    () => formatMeetingDateRange(startTime, endTime, undefined, timeFormat),
    [startTime, endTime, timeFormat]
  );

  // Parse invitations to guests (same pattern as MeetingSummary)
  const guests = useMemo(() => {
    if (!meetingObject) {
      return [];
    }
    const invitations = getPropertyValue<
      Array<{
        email?: string;
        invitee_name?: string;
        response_status?: string;
        is_organizer?: string;
        is_optional?: string;
      }>
    >(meetingObject.properties?.ahoy_invitations);
    return mapInvitationsToGuests(invitations);
  }, [meetingObject]);

  // Build email→CrmObject map for attendee→contact matching
  const contactsByEmail = useMemo(() => {
    const map = new Map<string, CrmObject>();

    for (const assoc of resolvedMeetingAssociations) {
      if (
        (assoc.association_type_name === 'ahoy_meeting_to_contact' ||
          assoc.association_type_name === 'ahoy_meeting_to_user') &&
        assoc.object
      ) {
        const rawEmails = getPropertyValue<string | string[]>(
          assoc.object.properties?.ahoy_email
        );
        let emails: string[] = [];
        if (Array.isArray(rawEmails)) {
          emails = rawEmails;
        } else if (typeof rawEmails === 'string') {
          emails = [rawEmails];
        }
        for (const email of emails) {
          if (typeof email === 'string' && email) {
            map.set(email.toLowerCase(), assoc.object);
          }
        }
      }
    }

    // Fallback: map user associations via org membership auth email
    for (const assoc of resolvedMeetingAssociations) {
      if (
        assoc.association_type_name === 'ahoy_meeting_to_user' &&
        assoc.object
      ) {
        const assocEntityId = assoc.object?.entity_id;
        const member = members.find(m => m.entityId === assocEntityId);
        if (member?.email && !map.has(member.email.toLowerCase())) {
          map.set(member.email.toLowerCase(), assoc.object);
        }
      }
    }

    return map;
  }, [resolvedMeetingAssociations, members]);

  const hasContent = meetingObject || timeStr || guests.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {/* Meeting — renders as clickable EntityBadge */}
      {meetingObject && (
        <OverviewFieldRow
          icon={Link03}
          label='Meeting'
          valueClassName='flex flex-wrap gap-1'
        >
          <ClickableEntityBadge
            object={meetingObject}
            onEntityClick={openSlideout ? handleEntityClick : undefined}
          />
        </OverviewFieldRow>
      )}

      {/* Time */}
      {timeStr && (
        <OverviewFieldRow
          icon={Clock}
          label='Time'
          valueClassName='text-xs md:text-sm font-normal text-fg-primary'
        >
          {timeStr}
        </OverviewFieldRow>
      )}

      {/* Attendees — matched contacts as EntityBadge, others as AttendeeChip */}
      {guests.length > 0 && (
        <OverviewFieldRow
          icon={Users01}
          label='Attendees'
          valueClassName='flex flex-wrap items-center gap-1.5'
        >
          {guests
            .filter(g => typeof g.email === 'string')
            .map(guest => {
              const contactObject = contactsByEmail.get(
                guest.email.toLowerCase()
              );

              if (contactObject) {
                return (
                  <ClickableEntityBadge
                    key={guest.email}
                    object={contactObject}
                    onEntityClick={openSlideout ? handleEntityClick : undefined}
                  />
                );
              }

              return <AttendeeChip key={guest.email} guest={guest} />;
            })}
        </OverviewFieldRow>
      )}
    </>
  );
}
